import WarehouseSelector, { useActiveWarehouse, WarehouseSelectionPopup } from 'src/components/atoms/WarehouseSelector';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { AxiosRequestConfig } from 'axios';
import { Alert, Box, TextField, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ClearIcon from '@mui/icons-material/Clear';
import RestorePageIcon from '@mui/icons-material/RestorePage';
import SaveIcon from '@mui/icons-material/Save';
import PageHeader from 'src/components/organisms/PageHeader';
import Footer from 'src/components/molecules/footer/Footer';
import CustomButton from 'src/components/atoms/buttons/CustomButton';
import ContentCardsContainer, { CardType, ContentCard } from 'src/components/organisms/ContentCard';
import { CustomDataGrid } from 'src/components/organisms/CustomDataGrid';
import useRequest from 'src/hooks/api/useRequest';
import { buildRelocationPayload, useRelocateProductsTable } from 'src/hooks/table/configs/useRelocateProductsTable';
import { commitPendingTableEdits, discardTableEdits, getTableSubmitRows, hasTableChanges } from 'src/hooks/table/tableSettings/tableEditActions';
import { generateTablePageButtons } from 'src/utils/generateTablePageButtons';
import { relocateProductsFromDefault } from 'src/utils/apiRequests';
import { RelocationOrderPrintControls, useRelocationOrderPrinting } from './RelocateProducts';

type Identifier = number | string;
type Notice = { severity: 'success' | 'error' | 'info'; message: string };
const scannerFilterId = 'relocate-product-scan';

export const RelocateReceivedProducts = () => {
    const { warehouseId, selectWarehouse } = useActiveWarehouse();
    const printing = useRelocationOrderPrinting('relocateReceivedProducts');
    const [refreshKey, setRefreshKey] = useState(0);
    const [savedCount, setSavedCount] = useState(0);
    const changeWarehouse = (value: Identifier) => { setSavedCount(0); selectWarehouse(value); };

    if (warehouseId == null || warehouseId === '') return (
        <>
            {printing.feedback}
            <WarehouseSelectionPopup />
            <PageHeader {...{ title: 'Relocate Received Products', subTitle: 'Move products to another warehouse location', components: [{ component: <WarehouseSelector value={warehouseId} onChange={changeWarehouse} /> }] }} />
            <ContentCardsContainer><ContentCard {...{ cardId: 'RelocateProducts', cardTypes: [CardType.noHeader] }}><Typography>Select a warehouse to relocate products.</Typography></ContentCard></ContentCardsContainer>
            <Footer />
        </>
    );

    // Recreate the station after a successful save or warehouse change. Old drafts
    // and request results cannot be carried into a different warehouse or batch.
    return <>{printing.feedback}<RelocationStation key={`${warehouseId}:${refreshKey}`} warehouseId={warehouseId} refreshKey={refreshKey} savedCount={savedCount} onWarehouseChange={changeWarehouse} printing={printing} onSaved={(count) => { setSavedCount(count); setRefreshKey((value) => value + 1); }} /></>;
};

const RelocationStation = ({ warehouseId, refreshKey, savedCount, onWarehouseChange, onSaved, printing }: { warehouseId: Identifier; refreshKey: number; savedCount: number; onWarehouseChange: (value: Identifier) => void; onSaved: (count: number) => void; printing: ReturnType<typeof useRelocationOrderPrinting> }) => {
    const [job, setJob] = useState<{ id: number; request: AxiosRequestConfig; count: number } | null>(null);
    const [saving, setSaving] = useState(false);
    const [tableColumns, tableSettings, data] = useRelocateProductsTable(warehouseId, refreshKey, job != null);
    const columns = useMemo(() => tableColumns.map((column) => column.field === 'EANCode' ? { ...column, headerName: 'Barcode(EAN)' } : column), [tableColumns]);
    const [scannerValue, setScannerValue] = useState('');
    const [notice, setNotice] = useState<Notice | null>(() => savedCount ? { severity: 'success', message: `Relocated ${savedCount} product row${savedCount === 1 ? '' : 's'}.` } : null);
    const scannerRef = useRef<HTMLInputElement>(null);
    const savingRef = useRef(false);
    const jobSequence = useRef(0);
    const mountedRef = useRef(true);
    const latestSettingsRef = useRef(tableSettings);
    latestSettingsRef.current = tableSettings;
    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

    const changes = hasTableChanges(tableSettings);
    const filterModel = tableSettings.filterModel ?? { items: [] };
    const quickFilterValues = filterModel.quickFilterValues ?? [];
    const quickFilterValue = quickFilterValues.join(' ');

    // Search identifiers even when their columns are hidden. Migrate a persisted
    // scanner filter from the old ProductId-only implementation to quick filtering.
    useEffect(() => {
        const legacyFilter = filterModel.items.find((item) => item.id === scannerFilterId);
        if (filterModel.quickFilterExcludeHiddenColumns === false && !legacyFilter) return;
        const legacyValue = String(legacyFilter?.value ?? '').trim();
        tableSettings.apiRef.current.setFilterModel({ ...filterModel, items: filterModel.items.filter((item) => item.id !== scannerFilterId), quickFilterValues: filterModel.quickFilterValues?.length ? filterModel.quickFilterValues : legacyValue ? [legacyValue] : [], quickFilterExcludeHiddenColumns: false });
    }, [filterModel, tableSettings.apiRef]);

    const clearScanFilter = () => {
        if (savingRef.current) return;
        const current = latestSettingsRef.current.filterModel ?? { items: [] };
        latestSettingsRef.current.apiRef.current.setFilterModel({ ...current, items: current.items.filter((item) => item.id !== scannerFilterId), quickFilterValues: [], quickFilterExcludeHiddenColumns: false });
        setScannerValue('');
        scannerRef.current?.focus();
    };

    const save = async () => {
        if (savingRef.current || tableSettings.loading) return;
        savingRef.current = true;
        setSaving(true);
        setNotice(null);
        try {
            const editRows = latestSettingsRef.current.apiRef.current.state.editRows;
            if (Object.values(editRows).some((cells) => Object.values(cells).some((cell) => cell.error || cell.isProcessingProps))) throw new Error('Correct the invalid cell before saving.');
            await commitPendingTableEdits(latestSettingsRef.current);
            if (!mountedRef.current) return;
            const editedRows = getTableSubmitRows(latestSettingsRef.current);
            const payload = buildRelocationPayload(editedRows, warehouseId, data.rows, data.locations);
            if (!payload.length) {
                savingRef.current = false;
                setSaving(false);
                setNotice({ severity: 'info', message: 'There are no changes to save.' });
                return;
            }
            setJob({ id: ++jobSequence.current, request: relocateProductsFromDefault(payload, warehouseId), count: payload.length });
        } catch (error) {
            if (!mountedRef.current) return;
            savingRef.current = false;
            setSaving(false);
            setNotice({ severity: 'error', message: error instanceof Error ? error.message : 'Could not prepare the relocation. Check the edited rows.' });
        }
    };

    const finishSave = (succeeded: boolean, response: unknown) => {
        if (!job) return;
        if (succeeded) { printing.onRelocated(response, warehouseId); onSaved(job.count); return; }
        setJob(null);
        savingRef.current = false;
        setSaving(false);
        setNotice({ severity: 'error', message: 'Relocation failed. Your edits have been kept.' });
    };

    const discard = () => {
        if (savingRef.current) return;
        discardTableEdits(latestSettingsRef.current);
        setNotice(null);
    };

    const changeWarehouse = (value: Identifier) => {
        if (savingRef.current || String(value) === String(warehouseId)) return;
        if (hasTableChanges(latestSettingsRef.current)) {
            setNotice({ severity: 'info', message: 'Save or discard your edits before changing warehouse.' });
            return;
        }
        onWarehouseChange(value);
    };

    const scan = (rawValue = scannerRef.current?.value ?? scannerValue) => {
        if (savingRef.current) return;
        const value = rawValue.trim();
        if (!value) return;
        setScannerValue('');
        if (value.toLowerCase() === 'cancel') { clearScanFilter(); return; }
        if (value.toLowerCase() === 'ok') { void save(); return; }
        const current = latestSettingsRef.current.filterModel ?? { items: [] };
        // Quick filter searches ProductId, ManufacturerProductId, EANCode and the
        // other searchable columns. Hidden, edited rows still belong to the batch.
        latestSettingsRef.current.apiRef.current.setFilterModel({ ...current, items: current.items.filter((item) => item.id !== scannerFilterId), quickFilterValues: [value], quickFilterExcludeHiddenColumns: false });
        setNotice(null);
        scannerRef.current?.focus();
    };

    const scanner = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField inputRef={scannerRef} value={scannerValue} disabled={saving} onChange={(event) => setScannerValue(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); scan((event.target as HTMLInputElement).value); } }} placeholder="Scan Product ID, Manufacturer ID, EAN, OK or Cancel" inputProps={{ 'aria-label': 'Scan product ID, manufacturer product ID, EAN, OK or Cancel' }} autoFocus sx={{ width: { xs: 180, sm: 300, md: 380 } }} />
            <CustomButton title="Scan" disabled={saving} size="small" iconButton={true} variant="outlined" icon={<ArrowForwardIcon />} onClick={() => scan()} />
        </Box>
    );

    return (
        <>
            {job && <RelocationRequest key={job.id} request={job.request} onComplete={finishSave} />}
            <PageHeader {...{
                title: 'Relocate Received Products',
                subTitle: 'Move products from the default receiving location to their warehouse locations',
                components: [
                    { position: 'center', component: scanner },
                    { component: <WarehouseSelector value={warehouseId} disabled={saving} onChange={changeWarehouse} /> },
                    { component: <CustomButton title="Clear" icon={<ClearIcon />} disabled={saving || !quickFilterValues.length} onClick={clearScanFilter} /> },
                    { component: <CustomButton title="Discard" color="error" icon={<RestorePageIcon />} disabled={saving || !changes} onClick={discard} /> },
                    { component: <CustomButton title="Save" icon={<SaveIcon />} isSubmitting={saving} disabled={saving || tableSettings.loading || !changes} onClick={() => { void save(); }} /> },
                    { component: <RelocationOrderPrintControls printing={printing} disabled={saving} /> },
                    ...generateTablePageButtons(tableSettings, {hideQuickSearch: true}),
                ],
            }} />
            {/* {notice && <Box sx={{ mx: 3, mb: 2 }}><Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.message}</Alert></Box>} */}
            {quickFilterValues.length > 0 && <Box sx={{ mx: 3, mb: 2 }}><Typography variant="body2">Quick filter: {quickFilterValue}. Save includes changes in all rows, including hidden rows.</Typography></Box>}
            <ContentCardsContainer><ContentCard {...{ cardId: 'RelocateProducts', md: 12, cardTypes: [CardType.noHeader], contentPadding: 0 }}><CustomDataGrid columns={columns} tableSettings={tableSettings} /></ContentCard></ContentCardsContainer>
            <Footer />
        </>
    );
};

// Each attempt gets a fresh request hook, so a previous result cannot complete
// a retry. Completion is based on status, including success with an empty body.
const RelocationRequest = ({ request, onComplete }: { request: AxiosRequestConfig; onComplete: (succeeded: boolean, response: unknown) => void }) => {
    const [response, setRequest, , loading, status] = useRequest({ logLevel: ['error', 'success'] });
    const startRef = useRef(() => setRequest(request));
    const startedRef = useRef(false);
    const completeRef = useRef(onComplete);
    const handledRef = useRef(false);
    completeRef.current = onComplete;
    useEffect(() => { if (!startedRef.current) { startedRef.current = true; startRef.current(); } }, []);
    useEffect(() => {
        const state: unknown = status;
        if (handledRef.current || loading || state === 'idle' || state === 'loading') return;
        const succeeded = state === 'success' || (typeof state === 'number' && state >= 200 && state < 300);
        const failed = state === 'error' || state === 'aborted' || (typeof state === 'number' && state >= 400);
        if (!succeeded && !failed) return;
        handledRef.current = true;
        completeRef.current(succeeded, response);
    }, [response, loading, status]);
    return null;
};


export default RelocateReceivedProducts;

export { default as WarehouseFilter } from 'src/components/atoms/WarehouseSelector';
